import {useRoute} from '@react-navigation/native';
import {View, ScrollView} from 'react-native';
import DetailNavTitle from '@/components/business/detail-nav-title';
import {goBack} from '@/utils';
import React from 'react';
import theme from '@/style';
import {useTranslation} from 'react-i18next';
import PageNumber from '@/components/business/games/page';
import Text from '@/components/basic/text';
import {
  LotteryHistoryTabItem,
  LotteryResultHistoryItem,
  getLottoResult,
} from '../../mix-lottery/mix-lottery-service';
import {INDEX_TO_O, RESULT_INDEX} from '../../mix-lottery/constant';
import DigitBall from '../../mix-lottery/component/digit-ball';
import Spin from '@/components/basic/spin';
import dayjs from 'dayjs';
import NoData from '@/components/basic/error-pages/no-data';
import ResultTab from '../../result/components/result-tab/result-tab';
import StateLotteryDraw from '../../result/state-lottery/draw';
import {BasicObject} from '@/types';
const pageSize = 10;
const MixLotteryDetail = ({
  navigation: _navigation,
}: {
  navigation: BasicObject;
}) => {
  const {params = {}} = useRoute();
  const {
    name,
    title,
    lotteryID = '',
    lotteryId = '',
    isQuick = '0',
    quick = '0',
  } = params as {
    name?: string;
    title?: string;
    lotteryID?: string;
    lotteryId?: string;
    isQuick?: string;
    quick?: string;
  };
  const [list, setList] = React.useState<LotteryResultHistoryItem[]>([]);
  const currentLotteryId = `${lotteryId || lotteryID}`;
  const currentQuick = quick || isQuick;
  const [lotteryIdState, setLotteryId] = React.useState(+currentLotteryId);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const {i18n} = useTranslation();
  const [tab, setTab] = React.useState<LotteryHistoryTabItem[]>([]);
  const [initPage, setInitPage] = React.useState(1);
  const isQuickGame = currentQuick === '1';
  const lotteryName = title || name;
  const requestRef = React.useRef(0);
  const scrollRef = React.useRef<ScrollView>(null);

  const getList = async (
    id: number,
    page: number = 1,
    quickType = isQuickGame,
  ) => {
    const requestId = ++requestRef.current;
    try {
      setLoading(true);
      const res = await getLottoResult(id, quickType, page, pageSize);
      if (requestId !== requestRef.current) {
        return;
      }
      if (res) {
        setList(res.rounds || []);
        setTotal(res.totalCount);
        setTab(res.tabs || []);
      } else {
        setList([]);
        setTotal(0);
        setTab([]);
      }
    } catch (_error) {
      if (requestId === requestRef.current) {
        setList([]);
        setTotal(0);
        setTab([]);
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  };

  React.useEffect(() => {
    if (lotteryName && lotteryIdState) {
      setInitPage(1);
      setList([]);
      setTotal(0);
      setTab([]);
      scrollRef.current?.scrollTo({y: 0, animated: false});
      getList(lotteryIdState, 1, isQuickGame).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotteryIdState, isQuickGame, lotteryName]);

  return (
    <Spin
      style={[theme.fill.fill, theme.background.lightGrey]}
      loading={loading}>
      <DetailNavTitle
        title={lotteryName}
        hideAmount
        serverRight
        onBack={() => goBack()}
      />
      {!loading && list && list.length === 0 ? <NoData /> : null}
      <View style={[theme.flex.flex1, theme.flex.basis0]}>
        <ScrollView
          ref={scrollRef}
          stickyHeaderIndices={isQuickGame ? [1] : []}
          showsVerticalScrollIndicator={false}
          style={[theme.flex.flex1]}>
          <View>
            {!loading ? <StateLotteryDraw isQuick={isQuickGame} /> : <View />}
          </View>
          {isQuickGame && (
            <ResultTab
              selectKey={`${lotteryIdState}`}
              onSelectKey={k => setLotteryId(+k)}
              tabs={tab.map(item => ({
                key: `${item.lotteryID}`,
                label: item.tabName.split('min')[0],
              }))}
            />
          )}
          {list && list.length > 0 && (
            <View
              style={[
                theme.flex.row,
                theme.flex.between,
                theme.padding.lrl,
                theme.margin.lrl,
                theme.flex.centerByCol,
                !isQuickGame && theme.margin.topl,
                // eslint-disable-next-line react-native/no-inline-styles
                {backgroundColor: '#e9ecf5', height: 32},
              ]}>
              <Text>{i18n.t('game-page.label.issue')}</Text>
              <Text>{i18n.t('game-page.label.number')}</Text>
            </View>
          )}
          <View>
            {list.map((item, _i) => {
              const backgroundColor = () => {
                return {
                  backgroundColor: _i % 2 === 0 ? '#FFF' : '#F8F9FF',
                };
              };
              return (
                <View
                  key={`${item.roundNo}-${_i}`}
                  style={[
                    theme.flex.row,
                    backgroundColor(),
                    theme.padding.l,
                    theme.margin.lrl,
                    theme.flex.centerByCol,
                  ]}>
                  <View style={[theme.flex.flex1]}>
                    <Text
                      color={theme.basicColor.dark}
                      fontWeight="500"
                      size="medium">
                      {item.roundNo}
                    </Text>
                    {!isQuickGame && (
                      <View style={theme.margin.topxxs}>
                        <Text fontWeight="500">
                          {dayjs(item.drawTime * 1000).format('DD-MM-YYYY')}
                        </Text>
                        <Text fontWeight="500">
                          {dayjs(item.drawTime * 1000).format('hh:mm A')}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={[{gap: theme.paddingSize.xxs}]}>
                    {item.results.map((pie, i) => (
                      <View
                        style={[theme.flex.row, theme.flex.between]}
                        key={i}>
                        <Text fontFamily="fontInterBold">
                          {INDEX_TO_O[i + 1]}-prize：
                        </Text>
                        <View
                          style={[
                            theme.flex.row,
                            theme.margin.leftxxs,
                            {gap: theme.paddingSize.xxs},
                          ]}>
                          {pie
                            .split('')
                            .slice(0, 6)
                            .map((ss, _ii) => (
                              <DigitBall
                                key={_ii}
                                disabled={
                                  RESULT_INDEX.slice(
                                    -pie.split('').slice(0, 6).length,
                                  )[_ii] === 'UNUSED'
                                }
                                digit={ss}
                                type={
                                  RESULT_INDEX.slice(
                                    -pie.split('').slice(0, 6).length,
                                  )[_ii]
                                }
                              />
                            ))}
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              );
            })}
          </View>
          {total > pageSize ? (
            <PageNumber
              total={total}
              page={initPage}
              pageSize={pageSize}
              onPageChange={p => {
                setInitPage(p);
                getList(lotteryIdState, p, isQuickGame).then();
              }}
            />
          ) : null}
        </ScrollView>
      </View>
    </Spin>
  );
};

export default MixLotteryDetail;
